import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Search, ArrowRight, X, LayoutGrid, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { COURSES, COURSE_DOMAINS } from "@/data/coursesData";
import CourseCard from "@/components/site/CourseCard";

const PAGE_SIZE = 9;

export default function Courses() {
  const [query, setQuery] = useState("");
  const [activeDomain, setActiveDomain] = useState("all");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  const handleApply = (course) => {
    toast.success(`Great choice! We've pre-filled "${course.title}" for you.`);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return COURSES.filter((c) => {
      const matchesDomain =
        activeDomain === "all" || c.domain === activeDomain || c.domainAlt === activeDomain;
      if (!matchesDomain) return false;
      if (!q) return true;
      return (
        c.title.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.skills.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [query, activeDomain]);

  const shown = filtered.slice(0, visible);

  const onSelectDomain = (key) => {
    setActiveDomain(key);
    setVisible(PAGE_SIZE);
  };

  return (
   <section
  id="courses"
  data-testid="courses-section"
  className="relative overflow-hidden bg-gradient-to-b from-[#0A3161] via-[#0A3161] to-[#1F2937] py-20 md:py-28"
>
      {/* Floating gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#B31942]/30 blur-[110px] animate-blob" />
        <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-full bg-indigo-500/25 blur-[130px] animate-blob" style={{ animationDelay: "3s" }} />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-cyan-400/20 blur-[110px] animate-blob" style={{ animationDelay: "6s" }} />
        <div className="absolute inset-0 grid-pattern opacity-[0.15]" />
      </div>

      <div className="container-x relative">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md px-3 py-1.5 text-[12px] font-semibold text-white/90">
              <Icons.Sparkles className="h-3.5 w-3.5 text-amber-300" /> {COURSES.length}+ Career-First Programs
            </div>
            <h2 className="mt-4 font-display font-extrabold text-4xl md:text-5xl tracking-tight text-white" data-testid="courses-heading">
              Programs built for <span className="bg-gradient-to-r from-amber-300 via-rose-300 to-fuchsia-300 bg-clip-text text-transparent">real careers</span>.
            </h2>
            <p className="mt-4 text-white/60 text-lg">
              From integrated commerce & finance degrees to tech and business
              analytics — explore, filter and apply in minutes.
            </p>
          </motion.div>
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            data-testid="courses-brochure"
            onClick={() => scrollTo("#contact")}
            className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#B31942] to-[#940835] text-white px-5 py-3 text-sm font-semibold shadow-[0_10px_30px_rgba(179,25,66,0.4)] transition-shadow hover:shadow-[0_14px_40px_rgba(179,25,66,0.5)] self-start md:self-auto"
          >
            Download Brochure <ArrowRight className="h-4 w-4" />
          </motion.button>
        </div>

        {/* Search + filters, glass panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-10 rounded-3xl border border-white/10 bg-white/[0.06] backdrop-blur-xl p-4 md:p-5 shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
        >
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <input
                data-testid="courses-search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setVisible(PAGE_SIZE);
                }}
                placeholder="Search courses, skills, certifications…"
                className="w-full h-12 rounded-2xl bg-white/5 border border-white/10 pl-11 pr-10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#B31942]/50 focus:border-white/20 transition-colors"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="inline-flex items-center gap-2 text-white/50 text-xs font-semibold shrink-0">
              <SlidersHorizontal className="h-3.5 w-3.5" /> {filtered.length} programs found
            </div>
          </div>

          {/* Category pills */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => onSelectDomain("all")}
              data-testid="course-tab-all"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm font-semibold border transition-all ${
                activeDomain === "all"
                  ? "bg-white text-[#0A3161] border-white shadow-lg"
                  : "bg-white/5 text-white/70 border-white/10 hover:border-white/30 hover:text-white"
              }`}
            >
              <LayoutGrid className="h-3.5 w-3.5" /> All Programs
            </button>
            {COURSE_DOMAINS.map((d) => {
              const Icon = Icons[d.icon] || Icons.Sparkles;
              const isActive = activeDomain === d.key;
              return (
                <button
                  key={d.key}
                  onClick={() => onSelectDomain(d.key)}
                  data-testid={`course-tab-${d.key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs md:text-sm font-semibold border transition-all ${
                    isActive
                      ? `bg-white-to-r ${d.gradient} text-white border-transparent shadow-lg`
                      : "bg-white/5 text-white/70 border-white/10 hover:border-white/30 hover:text-white"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" /> {d.title}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Grid */}
        <div className="mt-10">
          <AnimatePresence mode="wait">
            {shown.length > 0 ? (
              <motion.div
                key={`${activeDomain}-${query}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {shown.map((course, i) => (
                  <CourseCard key={course.slug} course={course} index={i} onApply={handleApply} />
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-12 text-center"
              >
                <p className="text-white/70">No programs match your search just yet.</p>
                <button
                  onClick={() => {
                    setQuery("");
                    setActiveDomain("all");
                  }}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 text-white px-4 py-2 text-sm font-semibold hover:bg-white/20 transition-colors"
                >
                  Clear filters
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {visible < filtered.length && (
            <div className="mt-10 flex justify-center">
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                data-testid="courses-load-more"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-white px-6 py-3 text-sm font-semibold hover:bg-white/10 transition-colors"
              >
                Load More Programs <ArrowRight className="h-4 w-4" />
              </motion.button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
